import { AttachmentIcon } from 'assets/icons/AttachmentIcon'
import { DeleteIcon } from 'assets/icons/DeleteIcon'
import axios from 'axios'
import { ImagePreview } from 'components/ImagePreview/ImagePreview'
import { JustSpinner } from 'components/Loading/Loading'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { colors, Flex, Icon } from 'styling/GlobalStyles'
import { ColorVariant, SmallText } from 'styling/TypographyV2'
import { formatBytes } from 'utils/helpers'

const getFileNameFromUrl = (url: string) => {
  return url.split('/').pop()?.split('?')[0] || 'image.jpg'
}

export const UploadedFile = ({
  file,
  onDelete,
}: {
  file: File | string
  onDelete?: () => void
}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [fileToDisplay, setFileToDisplay] = useState<File | null>(null)

  const getFileFromUrl = async (url: string) => {
    setIsLoading(true)
    const response = await axios.get(url, {
      responseType: 'blob',
    })

    setIsLoading(false)
    return new File([response.data], getFileNameFromUrl(url), {
      type: response.data.type || 'image/jpeg',
    })
  }

  useEffect(() => {
    if (typeof file === 'string') {
      getFileFromUrl(file).then((file) => {
        setFileToDisplay(file)
      })
    } else {
      setFileToDisplay(file)
    }
  }, [file])

  return (
    <Container>
      <Flex gap='0.75rem' center>
        <ImagePreview src={fileToDisplay && URL.createObjectURL(fileToDisplay)}>
          <FileIcon>
            {isLoading ? (
              <JustSpinner />
            ) : (
              fileToDisplay && (
                <ImageIcon src={URL.createObjectURL(fileToDisplay)} alt={fileToDisplay?.name} />
              )
            )}
          </FileIcon>
        </ImagePreview>
        <TextContainer>
          <SmallText>{fileToDisplay?.name}</SmallText>
          <SmallText colorVariant={ColorVariant.SECONDARY}>
            {formatBytes(fileToDisplay?.size || 0)}
          </SmallText>
        </TextContainer>
      </Flex>
      {onDelete && (
        <Icon fill={colors.red} size='1.25rem' onClick={onDelete}>
          {DeleteIcon}
        </Icon>
      )}
    </Container>
  )
}

export const SmallImage = ({ file }: { file: File | string }) => {
  const fileToDisplay = typeof file === 'string' ? file : URL.createObjectURL(file)
  const fileName = typeof file === 'string' ? getFileNameFromUrl(file) : file.name

  return (
    <ImagePreview src={fileToDisplay}>
      <FileIcon>
        <ImageIcon src={fileToDisplay} alt={fileName} />
      </FileIcon>
    </ImagePreview>
  )
}

const Container = styled.div`
  padding: 0.75rem;
  border-radius: 12px;
  justify-content: space-between;

  display: flex;
  align-items: center;
  gap: 0.75rem;

  ${(props) => props.theme.boxContainer}
  border: 1px solid ${(props) => props.theme.tableRowBorder};
`

const FileIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: #4f4f4f;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: ${(props) => props.theme.secondaryTextColor};
`
const TextContainer = styled.div`
  margin-right: 20px;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ImageIcon = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`
